import type { Question, Section } from '@voice2me/shared';
import { newId } from './api.js';
import { QuestionEditor, questionOfType } from './QuestionEditor.js';

/** A fresh, empty section ready to be titled and filled in the editor. */
export function emptySection(): Section {
  return { kind: 'section', id: newId('sec'), title: '', description: '', questions: [] };
}

type Props = {
  section: Section;
  index: number;
  total: number;
  onChange: (section: Section) => void;
  onRemove: () => void;
  onMove: (direction: -1 | 1) => void;
};

export function SectionEditor({ section, index, total, onChange, onRemove, onMove }: Props) {
  const updateQuestion = (questionIndex: number, question: Question) =>
    onChange({
      ...section,
      questions: section.questions.map((candidate, position) =>
        position === questionIndex ? question : candidate,
      ),
    });

  const removeQuestion = (questionIndex: number) =>
    onChange({
      ...section,
      questions: section.questions.filter((_unused, position) => position !== questionIndex),
    });

  const moveQuestion = (questionIndex: number, direction: -1 | 1) => {
    const questions = [...section.questions];
    const target = questionIndex + direction;
    const moving = questions[questionIndex];
    const displaced = questions[target];
    if (!moving || !displaced) return;
    questions[questionIndex] = displaced;
    questions[target] = moving;
    onChange({ ...section, questions });
  };

  const addQuestion = () =>
    onChange({ ...section, questions: [...section.questions, questionOfType('multiple_choice')] });

  return (
    <div className="section-editor">
      <div className="section-editor-header">
        <strong>Section {index + 1}</strong>
        <div className="question-editor-actions">
          <button type="button" onClick={() => onMove(-1)} disabled={index === 0}>
            Move up
          </button>
          <button type="button" onClick={() => onMove(1)} disabled={index === total - 1}>
            Move down
          </button>
          <button type="button" onClick={onRemove}>
            Remove section
          </button>
        </div>
      </div>

      <label>
        Section title
        <input
          type="text"
          value={section.title}
          placeholder="e.g. About your team"
          onChange={(event) => onChange({ ...section, title: event.target.value })}
        />
      </label>

      <label>
        Description (optional)
        <textarea
          rows={2}
          value={section.description ?? ''}
          onChange={(event) => onChange({ ...section, description: event.target.value })}
        />
      </label>

      {section.questions.map((question, questionIndex) => (
        <QuestionEditor
          key={question.id}
          question={question}
          index={questionIndex}
          total={section.questions.length}
          onChange={(updated) => updateQuestion(questionIndex, updated)}
          onMove={(direction) => moveQuestion(questionIndex, direction)}
          onRemove={() => removeQuestion(questionIndex)}
        />
      ))}

      {section.questions.length === 0 && (
        <p className="note">This section has no questions yet.</p>
      )}

      <button type="button" onClick={addQuestion}>
        Add question to section
      </button>
    </div>
  );
}
